import type { AxiosInstance } from "axios";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { useState } from "react";

import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { adminLogin } from "../lib/admin.js";
import { apiErrorMessage } from "../lib/api.js";
import { Logo } from "../components/Logo.js";

/**
 * Staff sign-in. A distinct look from the merchant login — dark, unmistakably "internal"
 * — so nobody confuses the portal that reaches every organization with the customer app.
 *
 * THE ERROR IS WHATEVER THE SERVER SAYS, one message for every failure, for the same
 * enumeration reason as the merchant login: a bad password and an unknown address must be
 * indistinguishable.
 */
export function AdminLoginScreen({
  client,
  onSignedIn,
}: {
  readonly client: AxiosInstance;
  readonly onSignedIn: () => void;
}): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminLogin(client, email, password);
      onSignedIn();
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not sign in."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-svh place-items-center bg-slate-950 px-4 text-slate-100">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <span className="flex size-12 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-400">
            <ShieldCheck className="size-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold">
              <Logo className="inline-block h-7 w-auto align-middle" />
              <span className="ml-2 rounded-md bg-slate-800 px-2 py-0.5 align-middle text-xs font-medium tracking-wide text-slate-300 uppercase">
                Admin
              </span>
            </h1>
            <p className="mt-1 text-sm text-slate-400">Staff portal. Signs out hourly.</p>
          </div>
        </div>

        <form className="flex flex-col gap-5" onSubmit={(e) => void submit(e)}>
          <div className="grid gap-1.5">
            <Label htmlFor="email" className="text-slate-300">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              className="h-11 border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="password" className="text-slate-300">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={reveal ? "text" : "password"}
                autoComplete="current-password"
                required
                className="h-11 border-slate-700 bg-slate-900 pr-10 text-slate-100"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setReveal((c) => !c)}
                aria-label={reveal ? "Hide password" : "Show password"}
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition-colors hover:text-slate-100"
              >
                {reveal ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
              </button>
            </div>
          </div>

          {error !== null && (
            <p className="text-sm text-red-400" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" disabled={busy} className="h-11 w-full bg-indigo-600 text-white hover:bg-indigo-500">
            {busy && <Loader2 className="animate-spin" />}
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </main>
  );
}
